import { FunctionComponent, lazy } from 'react'
import LoginPage from './AuthenticationPage/LoginPage'
import AuthenticationPage from './AuthenticationPage/AuthenticationPage'
import NewScriptPage from './NewScriptPage/NewScriptPage'
import PatientsPage from './PatientsPage/PatientsPage'
import PrescriptionsPage from './PrescriptionsPage/PrescriptionsPage'
import PrescriberProfileCreationPage from './PrescriberProfileCreationPage/PrescriberProfileCreationPage'
import GenericErrorPage from './ErrorPage/GenericErrorPage'
import LoadingPage from './LoadingPage'
import OrganizationCreationDeveloperPage from './OrganizationCreationDeveloperPage/OrganizationCreationDeveloperPage'
import ClinicCreationPage from './ClinicCreationPage/ClinicCreationPage'
import OrganizationLinkPage from './OrganizationLinkPage/OrganizationLinkPage'
import { ReferralPage } from '../features/refer/ReferralPage'
import { ExperimentsPage } from '../features/experiments/ExperimentsPage'
import { SSOLandingPage } from '../features/sso/SSOLandingPage'
import { SelectRolesPage } from '../features/select-roles/SelectRolesPage'
import AcceptInvitationPage from '../features/organization-membership/AcceptInvitationPage'
import CreateOrganizationAdminProfilePage from '../features/organization-admin/CreateOrganizationAdminProfilePage'
import { ApproveInvitationPage } from '../features/organization-membership/ApproveInvitationPage'
import { ApproveJoinRequestPage } from '../features/organization-membership/ApproveJoinRequestPage'
import { DataReportsPage } from '../features/data-export/DataReportsPage'
import { EhrCompletePage } from '../features/ehr-launch/EhrCompletePage'
import { EhrLaunchPage } from '../features/ehr-launch/EhrLaunchPage'
import SettingsPage from './SettingsPage/SettingsPage'
import OrganizationJoinResponsePage from './OrganizationJoinResponsePage/OrganizationJoinResponsePage'
import OrganizationCreationPage from './OrganizationCreationPage/OrganizationCreationPage'
import JoinOrganizationPendingPage from './JoinOrganizationPendingPage/JoinOrganizationPendingPage'

const PricePlanPage = lazy(() => import('./Billing/PricePlanPage'))
const PricePlansPage = lazy(() => import('./Billing/PricePlansPage'))
const SetDefaultPaymentMethodGuidePage = lazy(() =>
  import('./Billing/SetDefaultPaymentMethodGuidePage').then((module) => ({
    default: module.SetDefaultPaymentMethodGuidePage,
  }))
)

export interface AppRoute {
  path: string
  Component: FunctionComponent
  requiresAuth?: boolean
}

export const routes = {
  acceptUserInvitation: {
    path: '/invitations/accept',
    Component: AcceptInvitationPage,
    requiresAuth: false,
  },
  approveUserInvitation: {
    path: '/invitations/approve',
    Component: ApproveInvitationPage,
    requiresAuth: true,
  },
  approveJoinRequest: {
    path: '/join-requests/approve',
    Component: ApproveJoinRequestPage,
    requiresAuth: true,
  },
  authWithClientId: {
    path: '/auth/:clientId',
    Component: AuthenticationPage,
    requiresAuth: false,
  },
  joinOrganizationPending: {
    path: '/join-organization-pending',
    Component: JoinOrganizationPendingPage,
    requiresAuth: true,
  },
  createClinic: {
    path: '/create-organization/new-clinic',
    Component: ClinicCreationPage,
    requiresAuth: true,
  },
  createOrganizationDeveloper: {
    path: '/admin-tools/crt-org',
    Component: OrganizationCreationDeveloperPage,
    requiresAuth: false,
  },
  createOrganization: {
    path: '/create-organization',
    Component: OrganizationCreationPage,
    requiresAuth: true,
  },
  createPrescriberProfile: {
    path: '/create-prescriber-profile',
    Component: PrescriberProfileCreationPage,
    requiresAuth: true,
  },
  createOrganizationAdminProfile: {
    path: '/create-admin-profile',
    Component: CreateOrganizationAdminProfilePage,
    requiresAuth: true,
  },
  error: {
    path: '/error',
    Component: GenericErrorPage,
    requiresAuth: false,
  },
  ehrLaunch: {
    path: '/launch',
    Component: EhrLaunchPage,
    requiresAuth: false,
  },
  ehrComplete: {
    path: '/auth/ehr-complete',
    Component: EhrCompletePage,
    requiresAuth: false,
  },
  experiments: {
    path: '/experiments',
    Component: ExperimentsPage,
    requiresAuth: true,
  },
  linkOrganization: {
    path: '/create-organization/link-to-clinic',
    Component: OrganizationLinkPage,
    requiresAuth: true,
  },
  organizationJoinResponse: {
    path: '/organization/join-response',
    Component: OrganizationJoinResponsePage,
    requiresAuth: true,
  },
  login: {
    path: '/login',
    Component: LoginPage,
    requiresAuth: false,
  },
  newScript: {
    path: '/new-script',
    Component: NewScriptPage,
    requiresAuth: true,
  },
  patients: {
    path: '/patients',
    Component: PatientsPage,
    requiresAuth: true,
  },
  patientScripts: {
    path: '/prescribers/:prescriberId/patients/:patientId/scripts',
    Component: PrescriptionsPage,
    requiresAuth: true,
  },
  prescriptions: {
    path: '/prescriptions',
    Component: PrescriptionsPage,
    requiresAuth: true,
  },
  pricePlan: {
    path: '/price-plans',
    Component:
      process.env.REACT_APP_FEATURE_TOGGLE_NEW_PLAN_ENABLED === 'true'
        ? PricePlansPage
        : PricePlanPage,
    requiresAuth: true,
  },
  refer: {
    path: '/refer',
    Component: ReferralPage,
    requiresAuth: true,
  },
  root: {
    path: '/',
    Component: LoadingPage,
    requiresAuth: true,
  },
  selectRoles: {
    path: '/select-roles',
    Component: SelectRolesPage,
    requiresAuth: true,
  },
  settings: {
    path: '/settings',
    Component: SettingsPage,
    requiresAuth: true,
  },
  dataReports: {
    path: '/data-reports',
    Component: DataReportsPage,
    requiresAuth: true,
  },
  setDefaultPaymentMethodGuide: {
    path: '/how-to-set-default-payment-method',
    Component: SetDefaultPaymentMethodGuidePage,
    requiresAuth: true,
  },
  sso: {
    path: '/sso/:ssoClientNameParam',
    Component: SSOLandingPage,
    requiresAuth: false,
  },
}
